import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { 
  Calendar, Radio, Video, Users, Eye, 
  Globe, MousePointerClick, Clock, Download
} from "lucide-react";
import axios from "axios";
import { API } from "./utils";

export const OverviewTab = ({ stats, liveStreams }) => {
  const [analytics, setAnalytics] = useState(null);
  const [realtimeViewers, setRealtimeViewers] = useState({ logged_in_viewers: 0 });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const [analyticsRes, realtimeRes] = await Promise.all([
          axios.get(`${API}/analytics/overview?time_range=${timeRange}`).catch(() => ({ data: null })),
          axios.get(`${API}/analytics/viewers/realtime`).catch(() => ({ data: { logged_in_viewers: 0 } }))
        ]);

        if (analyticsRes.data) {
          setAnalytics(analyticsRes.data);
        }
        
        if (realtimeRes.data) {
          setRealtimeViewers(realtimeRes.data);
        }
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || "0";
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/analytics/export?time_range=${timeRange}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${timeRange}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export analytics. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [timeRange]);

  const getRangeLabel = (range) => {
    switch(range) {
      case "today": return "Today";
      case "24h": return "24H";
      case "7d": return "7D";
      case "30d": return "30D";
      case "all": return "All";
      default: return range.toUpperCase();
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header with Time Range Selector and Export */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
          Analytics Dashboard
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {["today", "24h", "7d", "30d", "all"].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 text-xs font-bold uppercase transition-colors ${
                  timeRange === range
                    ? "bg-orange-500 text-black"
                    : "bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
                }`}
                data-testid={`time-range-${range}`}
              >
                {getRangeLabel(range)}
              </button>
            ))}
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase transition-colors disabled:opacity-50"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Primary Stats Grid - Row 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Games Today"
          value={stats?.total_games_today || 0}
          icon={Calendar}
          color="text-blue-500"
        />
        <StatCard
          label="Live Now"
          value={liveStreams?.length || 0}
          icon={Radio}
          color="text-red-500"
          highlight={liveStreams?.length > 0}
        />
        <StatCard
          label="Scheduled"
          value={stats?.upcoming_broadcasts || 0}
          icon={Video}
          color="text-green-500"
        />
        <StatCard
          label="Total Users"
          value={analytics?.users?.total || 0}
          icon={Users}
          color="text-purple-500"
          subtext={analytics?.users?.new > 0 ? `+${analytics.users.new} new` : null}
          formatValue={formatNumber}
        />
      </div>

      {/* Live Streams Alert */}
      {liveStreams?.length > 0 && (
        <div className="p-4 bg-red-500/10 border border-red-500/20">
          <h3 className="font-secondary text-lg font-bold text-red-500 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            Currently Live
          </h3>
          {liveStreams.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-2 border-t border-red-500/10 first:border-0">
              <div>
                <span className="text-white font-bold">{s.title}</span>
                <span className="text-zinc-500 text-sm ml-2">• {s.sport}</span>
              </div>
              <span className="text-xs text-red-500 uppercase font-bold">LIVE</span>
            </div>
          ))}
        </div>
      )}

      {/* Secondary Stats Grid - Row 2 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Active Viewers"
          value={realtimeViewers.logged_in_viewers}
          icon={Eye}
          color="text-orange-500"
          subtext="logged in now"
          formatValue={formatNumber}
        />
        <StatCard
          label="VOD Views"
          value={analytics?.content?.total_vod_views || 0}
          icon={Video}
          color="text-cyan-500"
          formatValue={formatNumber}
        />
        <StatCard
          label="Website Visits"
          value={analytics?.traffic?.website_visits || 0}
          icon={Globe}
          color="text-emerald-500"
          formatValue={formatNumber}
        />
        <StatCard
          label="Ad Banner Clicks"
          value={analytics?.traffic?.ad_clicks || 0}
          icon={MousePointerClick}
          color="text-yellow-500"
          formatValue={formatNumber}
        />
      </div>

      {/* Last Updated Footer */}
      <div className="p-4 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-orange-500" />
          <span className="text-sm text-zinc-400">Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
        <p className="text-zinc-500 text-xs">
          Analytics refresh automatically every 30 seconds. Export includes daily breakdown for the selected time range.
        </p>
      </div>
    </motion.div>
  );
};

// Reusable Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, highlight, subtext, formatValue }) => {
  const displayValue = formatValue ? formatValue(value) : value;
  
  return (
    <div 
      className={`p-6 border transition-colors ${
        highlight 
          ? 'bg-red-500/10 border-red-500/20' 
          : 'bg-zinc-900/50 border-white/5 hover:border-white/10'
      }`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className={`w-5 h-5 ${color} mb-4`} />
      <div className="font-secondary text-4xl font-black text-white">{displayValue}</div>
      <div className="text-xs text-zinc-500 uppercase tracking-widest">{label}</div>
      {subtext && (
        <div className="text-[10px] text-green-500 mt-1">{subtext}</div>
      )}
    </div>
  );
};

export default OverviewTab;
